/**
 * 华为应用商店：自定义盯盘策略配置 API 服务（CLAUDE.md §前端 API 服务层 — 按域分目录）。
 *
 * 与后端 Pydantic 模型 huawei.dtos.watch_strategy 对齐。
 *   - GET  /api/v1/huawei/watch-strategy?page=&size= → 策略分页列表
 *     （任意登录用户可读，按 id 倒序）
 *   - POST /api/v1/huawei/watch-strategy → 新建策略（仅 reizhu / v_summerxiao /
 *     admin / devinzhang，否则后端 403；入参不合法 400；写库失败 500；新建默认打开）
 *   - PUT  /api/v1/huawei/watch-strategy/{id} → 编辑条件组与操作
 *     （不动 create_user / is_enabled）
 *   - PUT  /api/v1/huawei/watch-strategy/{id}/status → 打开 / 关闭策略
 *   - DELETE /api/v1/huawei/watch-strategy/{id} → 软删除策略
 *     （is_deleted=1 并同时置 is_enabled=0；仅白名单用户，否则后端 403）
 *
 * 生效链路：写入的配置由后端判断引擎脚本（huawei-watch-strategy，周期性外部调度）
 * 读取 is_enabled=1 的策略做任务级判断，命中结果落库 t_huawei_auto_ad_operate_result
 * （本轮只判断 + 存储，不调华为 API 执行）。
 */

import { request } from "../apiClient";

/** 指标类别：numeric=数值/衍生/账户指标（须带时间周期）；attribute=属性指标（无时间周期）。 */
export type MetricType = "numeric" | "attribute";

/**
 * 阈值模式（与后端 dtos.watch_strategy 一致）：
 * fixed=固定值（默认，存量条件无此字段按此口径）；
 * target_sxfl_multiplier=「目标费率的 x 倍」——生效阈值 = 目标费率 × value
 * （x 存于 value，须为正数），仅授信费率指标（sxfl）可用；
 * target_cost_multiplier=「目标成本的 x 倍」——生效阈值 = 目标成本 cost_value（元）× value，
 * 仅企业进件成本指标（cost_ent_appl）可用。
 */
export type ValueMode = "fixed" | "target_sxfl_multiplier" | "target_cost_multiplier";

/** 阈值模式枚举值（与后端 VALUE_MODE_FIXED / VALUE_MODE_TARGET_SXFL_MULTIPLIER /
 * VALUE_MODE_TARGET_COST_MULTIPLIER 一致）。 */
export const VALUE_MODE_FIXED: ValueMode = "fixed";
export const VALUE_MODE_TARGET_SXFL_MULTIPLIER: ValueMode = "target_sxfl_multiplier";
export const VALUE_MODE_TARGET_COST_MULTIPLIER: ValueMode = "target_cost_multiplier";

/** 授信费率指标 key（衍生指标，支持「目标费率×x」模式，与后端 SXFL_METRIC_KEY 一致）。 */
export const SXFL_METRIC_KEY = "sxfl";
/** 允许「目标费率×x」倍率模式的指标集合（与后端 MULTIPLIER_ALLOWED_METRICS 双源一致）。 */
export const MULTIPLIER_ALLOWED_METRICS: readonly string[] = [
  SXFL_METRIC_KEY,
  "account_sxfl",
];

/** 企业进件成本指标 key（衍生指标，支持「目标成本×x」模式，与后端 COST_ENT_APPL_METRIC_KEY 一致）。 */
export const COST_ENT_APPL_METRIC_KEY = "cost_ent_appl";
/** 允许「目标成本×x」倍率模式的指标集合（与后端 COST_MULTIPLIER_ALLOWED_METRICS 双源一致）。 */
export const COST_MULTIPLIER_ALLOWED_METRICS: readonly string[] = [
  COST_ENT_APPL_METRIC_KEY,
  "account_cost_ent_appl",
];

/** 单个条件（与后端 ConditionItem 对齐；属性指标 period 必为 null）。 */
export interface ConditionItem {
  metric_type: MetricType;
  /** 时间周期（数值/衍生/账户指标必填，取值见 PERIOD_OPTIONS；属性指标为 null）。 */
  period: string | null;
  /** 指标 key（取值见 METRIC_OPTIONS；service 层校验与 metric_type 联动）。 */
  metric: string;
  operator: string;
  /** 数值/衍生/数值属性指标为数值；单值枚举（siteset/is_work_day）为枚举字符串；
   * 多值枚举（present_hour，见 MULTI_VALUE_ENUM_METRIC_KEYS）为枚举字符串数组
   * （非空子集，= 读作 ∈、!= 读作 ∉）。 */
  value: number | string | string[];
  /** 阈值模式（sxfl 可选 target_sxfl_multiplier；cost_ent_appl 可选
   * target_cost_multiplier；缺省按 fixed 口径）。 */
  value_mode?: ValueMode;
}

/** 盯盘策略当前态（与后端 WatchStrategyRecord 对齐；None 字段经 exclude_none 不下发）。 */
export interface WatchStrategyRecord {
  id: number;
  conditions: ConditionItem[];
  action_type: "reduce_budget" | "increase_budget" | "set_budget" | "stop_adgroup" | "warning";
  /** 操作参数（reduce_budget/increase_budget=百分比小数如 0.10=10%；
   * set_budget=目标预算金额元；stop_adgroup/warning 不下发）。 */
  action_value?: number;
  is_enabled: boolean;
  create_user?: string;
  modify_user?: string;
  created_time?: string;
  updated_time?: string;
}

/** 新建 / 编辑入参（POST / PUT）。 */
export interface SaveWatchStrategyBody {
  /** 条件组（1~7 组，组内 AND）。 */
  conditions: ConditionItem[];
  action_type: "reduce_budget" | "increase_budget" | "set_budget" | "stop_adgroup" | "warning";
  /** reduce_budget/increase_budget 必填 ((0,1) 开区间百分比小数)；
   * set_budget 必填 >0 金额元；stop_adgroup/warning 必须为 null。 */
  action_value: number | null;
}

/** 策略分页列表出参。 */
export interface WatchStrategyListResult {
  total: number;
  items: WatchStrategyRecord[];
}

/** 读取盯盘策略分页列表（任意登录用户可读；默认拉前 200 条全量回显）。 */
export async function listWatchStrategies(
  page = 1,
  size = 200,
): Promise<WatchStrategyListResult> {
  return request<WatchStrategyListResult>(
    `/api/v1/huawei/watch-strategy?page=${page}&size=${size}`,
  );
}

/** 新建盯盘策略（默认打开）；返回建后当前态。非白名单用户后端返回 403。 */
export async function createWatchStrategy(
  body: SaveWatchStrategyBody,
): Promise<WatchStrategyRecord> {
  return request<WatchStrategyRecord>("/api/v1/huawei/watch-strategy", {
    method: "POST",
    body,
  });
}

/** 编辑盯盘策略条件组与操作（不动开关态与首次创建人）；返回改后当前态。 */
export async function updateWatchStrategy(
  id: number,
  body: SaveWatchStrategyBody,
): Promise<WatchStrategyRecord> {
  return request<WatchStrategyRecord>(`/api/v1/huawei/watch-strategy/${id}`, {
    method: "PUT",
    body,
  });
}

/** 打开 / 关闭盯盘策略；返回改后当前态。 */
export async function toggleWatchStrategy(
  id: number,
  isEnabled: boolean,
): Promise<WatchStrategyRecord> {
  return request<WatchStrategyRecord>(
    `/api/v1/huawei/watch-strategy/${id}/status`,
    {
      method: "PUT",
      body: { is_enabled: isEnabled },
    },
  );
}

/** 软删除盯盘策略（is_deleted=1 且 is_enabled=0）；返回被删策略 id。 */
export async function deleteWatchStrategy(id: number): Promise<{ id: number }> {
  return request<{ id: number }>(`/api/v1/huawei/watch-strategy/${id}`, {
    method: "DELETE",
  });
}

/* ---------- 注册表中文映射（与后端 huawei.dtos.watch_strategy 严格一致） ----------
 * 编辑器下拉选项与列表「满足条件 / 执行操作」中文渲染共用同一来源，避免双源漂移。 */

export interface LabelOption {
  value: string;
  label: string;
}

/** 时间窗（10 个，与后端 PERIODS 一致；属性指标无时间周期）。 */
export const PERIOD_OPTIONS: LabelOption[] = [
  { value: "today", label: "今日" },
  { value: "3d", label: "近3个工作日(不含周末)" },
  { value: "3d_cont", label: "近3个自然日" },
  { value: "5d", label: "近5个工作日(不含周末)" },
  { value: "5d_cont", label: "近5个自然日" },
  { value: "10d", label: "近10个工作日(不含周末)" },
  { value: "10d_cont", label: "近10个自然日" },
  { value: "20d", label: "近20个工作日(不含周末)" },
  { value: "20d_cont", label: "近20个自然日" },
  { value: "mon", label: "本月至今" },
];

/** 数值 + 账户 + 衍生指标（28 个，编辑器「数值指标」下拉一组展示，
 * 与后端 NUMERIC_METRICS + ACCOUNT_NUMERIC_METRICS + DERIVED_METRICS 一致）。 */
export const NUMERIC_METRIC_OPTIONS: LabelOption[] = [
  // 基础数值指标（10 个）
  { value: "cost", label: "消耗" },
  { value: "download", label: "下载数" },
  { value: "convert_1_activate", label: "激活数" },
  { value: "convert_4_firstpay", label: "首次付费数" },
  { value: "convert_6_frontcredit", label: "前端授信数" },
  { value: "convert_7_register", label: "注册数" },
  { value: "ent_appl_cnt", label: "企业进件" },
  { value: "credit_cnt", label: "授信数" },
  { value: "credit_amt", label: "授信金额(万)" },
  { value: "sign_cnt", label: "签约数" },
  // 账户级数值指标（9 个）
  //  report 源 6 个 + d7d30 源 3 个
  { value: "account_cost", label: "账户整体消耗" },
  { value: "account_download", label: "账户整体下载数" },
  { value: "account_convert_1_activate", label: "账户整体激活数" },
  { value: "account_convert_4_firstpay", label: "账户整体首次付费数" },
  { value: "account_convert_6_frontcredit", label: "账户整体前端授信数" },
  { value: "account_convert_7_register", label: "账户整体注册数" },
  { value: "account_ent_appl_cnt", label: "账户整体企业进件" },
  { value: "account_credit_cnt", label: "账户整体授信数" },
  { value: "account_credit_amt", label: "账户整体授信金额(万)" },
  // 衍生指标（14 个）
  { value: "cost_ent_appl", label: "企业进件成本" },
  { value: "cost_credit", label: "授信成本" },
  { value: "sxfl", label: "授信费率" },
  { value: "cost_activate", label: "激活成本" },
  { value: "cost_firstpay", label: "首次付费成本" },
  { value: "cost_frontcredit", label: "前端授信成本" },
  { value: "cost_register", label: "注册成本" },
  { value: "cost_sign", label: "签约成本" },
  { value: "account_cost_activate", label: "账户整体激活成本" },
  { value: "account_cost_firstpay", label: "账户整体首次付费成本" },
  { value: "account_cost_frontcredit", label: "账户整体前端授信成本" },
  { value: "account_cost_register", label: "账户整体注册成本" },
  { value: "account_sxfl", label: "账户整体授信费率" },
  { value: "account_cost_ent_appl", label: "账户整体企业进件成本" },
];

/** 属性指标（1 个，无时间周期，与后端 ATTRIBUTE_METRICS 一致）。
 *  注：is_work_day 与 present_hour 已迁移至 ENUM_ATTRIBUTE_METRIC_OPTIONS
 * （is_work_day 值 0/1 为单值枚举；present_hour 为 "0"~"23" 多值枚举）。 */
export const ATTRIBUTE_METRIC_OPTIONS: LabelOption[] = [
  { value: "present_day", label: "当月天日期" },
];

/** 枚举属性指标（无时间周期，值为字符串枚举，仅支持 = / !=，与后端
 * ENUM_ATTRIBUTE_METRICS 一致）。 */
export const ENUM_ATTRIBUTE_METRIC_OPTIONS: LabelOption[] = [
  { value: "siteset", label: "任务类型" },
  { value: "is_work_day", label: "是否工作日" },
  { value: "present_hour", label: "当天小时段" },
];

/** siteset 合法枚举值（与后端 SITESET_VALUES 一致）。 */
export const SITESET_VALUES: readonly string[] = [
  "搜索", "推荐", "贷款专区", "贷专", "智投", "鸿蒙", "卸载召回", "卸召",
];

/** present_hour 合法枚举值："0"~"23"（与后端 PRESENT_HOUR_VALUES 一致）。 */
export const PRESENT_HOUR_VALUES: readonly string[] = Array.from(
  { length: 24 },
  (_, h) => String(h),
);

/** 枚举指标 key → 合法值清单（与后端 ENUM_METRIC_VALUES 一致，预留扩展）。 */
export const ENUM_METRIC_VALUES: Record<string, readonly string[]> = {
  siteset: SITESET_VALUES,
  is_work_day: ["0", "1"],
  present_hour: PRESENT_HOUR_VALUES,
};

/** 多值枚举指标 key 集合（与后端 MULTI_VALUE_ENUM_METRICS 一致）：
 *  value 为枚举值字符串数组（非空子集），= 读作 ∈、!= 读作 ∉；
 *  编辑器以多选 chips 承载。 */
export const MULTI_VALUE_ENUM_METRIC_KEYS: ReadonlySet<string> = new Set([
  "present_hour",
]);

/** 枚举属性指标仅支持的操作符（与后端 ENUM_OPERATORS 一致）。 */
export const ENUM_OPERATOR_OPTIONS: readonly string[] = ["=", "!="];

/** 枚举属性指标 key 集合（用于前端快速判别：属性指标下拉中哪些是枚举类型）。 */
export const ENUM_ATTRIBUTE_METRIC_KEYS: ReadonlySet<string> = new Set(
  ENUM_ATTRIBUTE_METRIC_OPTIONS.map((o) => o.value),
);

/** 操作符（6 个，与后端 OPERATORS 一致）。 */
export const OPERATOR_OPTIONS: string[] = ["=", "!=", ">", ">=", "<", "<="];

/** 阈值模式选项（仅 sxfl 授信费率条件行展示，与后端 VALUE_MODES 一致）。 */
export const VALUE_MODE_OPTIONS: LabelOption[] = [
  { value: VALUE_MODE_FIXED, label: "固定值" },
  { value: VALUE_MODE_TARGET_SXFL_MULTIPLIER, label: "目标费率×" },
];

/** 成本倍率阈值模式选项（仅 cost_ent_appl 企业进件成本条件行展示）。 */
export const COST_VALUE_MODE_OPTIONS: LabelOption[] = [
  { value: VALUE_MODE_FIXED, label: "固定值" },
  { value: VALUE_MODE_TARGET_COST_MULTIPLIER, label: "目标成本×" },
];

/** 操作类型（5 个，与后端 ACTION_TYPES 一致；
 * 预警仅记录命中供后续告警，无参数）。 */
export const ACTION_TYPE_OPTIONS: LabelOption[] = [
  { value: "reduce_budget", label: "预算降低" },
  { value: "increase_budget", label: "预算提升" },
  { value: "set_budget", label: "预算调整至" },
  { value: "stop_adgroup", label: "暂停投放" },
  { value: "warning", label: "预警" },
];

/** 单策略条件组上限（与后端 MAX_CONDITIONS 一致）。 */
export const MAX_CONDITIONS = 7;

const PERIOD_LABELS = new Map(PERIOD_OPTIONS.map((o) => [o.value, o.label]));
const METRIC_LABELS = new Map(
  [...NUMERIC_METRIC_OPTIONS, ...ATTRIBUTE_METRIC_OPTIONS, ...ENUM_ATTRIBUTE_METRIC_OPTIONS].map((o) => [o.value, o.label]),
);
const ACTION_TYPE_LABELS = new Map(ACTION_TYPE_OPTIONS.map((o) => [o.value, o.label]));

/** 生效阈值格式化：至多 6 位小数去尾零（0.012 → "0.012"）。 */
export function formatThreshold(v: number): string {
  const rounded = roundTo(v, 6);
  if (rounded === Math.trunc(rounded)) return String(Math.trunc(rounded));
  return String(rounded).replace(/0+$/, "").replace(/\.$/, "");
}

/** 四舍五入到指定位小数（避免浮点舍入误差——Math.round 在挂零场景的尾差）。 */
function roundTo(v: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(v * factor) / factor;
}

/**
 * 单条件中文描述（列表「满足条件」列展示，不含实际值）：
 * 数值/衍生/账户指标带周期前缀「近3个工作日(不含周末)·注册成本>50」；属性指标无前缀「是否工作日=1」。
 * 多值枚举（present_hour）渲染为「当天小时段∈{14,15}」（operator != 渲染 ∉）。
 * value_mode='target_sxfl_multiplier'（sxfl）时阈值段渲染为「目标费率×{x}」，
 * 传入 targetSxfl（当前目标费率）时追加生效阈值「(=0.012)」；
 * value_mode='target_cost_multiplier'（cost_ent_appl）时渲染为「目标成本×{x}」，
 * 传入 targetCost（当前目标成本，元）时追加生效阈值「(=6000)」。
 */
export function describeCondition(
  c: ConditionItem,
  targetSxfl?: number,
  targetCost?: number,
): string {
  const metricLabel = METRIC_LABELS.get(c.metric) ?? c.metric;
  const prefix =
    c.metric_type !== "attribute" && c.period
      ? `${PERIOD_LABELS.get(c.period) ?? c.period}·`
      : "";
  if (Array.isArray(c.value)) {
    const opText = c.operator === "!=" ? "∉" : "∈";
    return `${prefix}${metricLabel}${opText}{${c.value.join(",")}}`;
  }
  if (c.value_mode === VALUE_MODE_TARGET_SXFL_MULTIPLIER) {
    const multiplier = Number(c.value);
    const effective =
      targetSxfl !== undefined && Number.isFinite(multiplier)
        ? `(=${formatThreshold(targetSxfl * multiplier)})`
        : "";
    return `${prefix}${metricLabel}${c.operator}目标费率×${c.value}${effective}`;
  }
  if (c.value_mode === VALUE_MODE_TARGET_COST_MULTIPLIER) {
    const multiplier = Number(c.value);
    const effective =
      targetCost !== undefined && Number.isFinite(multiplier)
        ? `(=${formatThreshold(targetCost * multiplier)})`
        : "";
    return `${prefix}${metricLabel}${c.operator}目标成本×${c.value}${effective}`;
  }
  return `${prefix}${metricLabel}${c.operator}${c.value}`;
}

/**
 * 单条件文案的「前缀/数值/后缀」拆段（脑图数值内联编辑用，与 describeCondition
 * 同口径：prefix + valueText + suffix === describeCondition(c, targetSxfl, targetCost)）。
 * 仅数值可编辑条件（value 为 number：数值/衍生/账户指标及数值属性 present_day）
 * 返回非 null；枚举/多值枚举（value 为 string/string[]）返回 null。
 * 倍率模式：prefix 含「目标费率×」/「目标成本×」，valueText 为倍数 x，suffix 为
 * 基线生效值「(=0.012)」（基线未传时为空）。
 */
export function describeConditionParts(
  c: ConditionItem,
  targetSxfl?: number,
  targetCost?: number,
): { prefix: string; valueText: string; suffix: string } | null {
  if (typeof c.value !== "number") return null;
  const metricLabel = METRIC_LABELS.get(c.metric) ?? c.metric;
  const prefix =
    c.metric_type !== "attribute" && c.period
      ? `${PERIOD_LABELS.get(c.period) ?? c.period}·`
      : "";
  if (c.value_mode === VALUE_MODE_TARGET_SXFL_MULTIPLIER) {
    const multiplier = c.value;
    const effective =
      targetSxfl !== undefined && Number.isFinite(multiplier)
        ? `(=${formatThreshold(targetSxfl * multiplier)})`
        : "";
    return {
      prefix: `${prefix}${metricLabel}${c.operator}目标费率×`,
      valueText: String(c.value),
      suffix: effective,
    };
  }
  if (c.value_mode === VALUE_MODE_TARGET_COST_MULTIPLIER) {
    const multiplier = c.value;
    const effective =
      targetCost !== undefined && Number.isFinite(multiplier)
        ? `(=${formatThreshold(targetCost * multiplier)})`
        : "";
    return {
      prefix: `${prefix}${metricLabel}${c.operator}目标成本×`,
      valueText: String(c.value),
      suffix: effective,
    };
  }
  return {
    prefix: `${prefix}${metricLabel}${c.operator}`,
    valueText: String(c.value),
    suffix: "",
  };
}

/**
 * 操作中文描述（列表「执行操作」列展示）：
 * 「预算降低 10%」/「预算提升 20%」/「预算调整至 5000」/「暂停投放」/「预警」。
 */
export function describeAction(
  actionType: string,
  actionValue: number | null | undefined,
): string {
  const base = ACTION_TYPE_LABELS.get(actionType) ?? actionType;
  if (actionType === "reduce_budget" || actionType === "increase_budget") {
    return `${base} ${Math.round((actionValue ?? 0) * 100)}%`;
  }
  if (actionType === "set_budget") {
    return `${base} ${actionValue ?? 0}`;
  }
  return base;
}

/**
 * 操作文案的「前缀/数值/后缀」拆段（脑图数值内联编辑用，与 describeAction 同口径：
 * prefix + valueText + suffix === describeAction(actionType, actionValue)）。
 * 仅带数值参数的操作（预算降低/提升/调整至）返回非 null；暂停投放/预警返回 null。
 * valueText 口径：reduce/increase 为百分比数字（0.10 → "10"，与展示一致）；
 * set_budget 为目标金额（元）。
 */
export function describeActionParts(
  actionType: string,
  actionValue: number | null | undefined,
): { prefix: string; valueText: string; suffix: string } | null {
  const base = ACTION_TYPE_LABELS.get(actionType) ?? actionType;
  if (actionType === "reduce_budget" || actionType === "increase_budget") {
    return {
      prefix: `${base} `,
      valueText: String(Math.round((actionValue ?? 0) * 100)),
      suffix: "%",
    };
  }
  if (actionType === "set_budget") {
    return { prefix: `${base} `, valueText: String(actionValue ?? 0), suffix: "" };
  }
  return null;
}